<template>
    <div>
        <div class="table-controls | u-text-right">
            <div class="page-block__header">
                <h1 class="tabulator__title">{{ trans('page.subscriptions.title') }}</h1>
                <button class="btn btn--ghost btn--small" @click="downloadCsv">
                    {{ trans('action.download.csv') }}
                </button>
            </div>
        </div>
        <div class="tabulator">
            <div ref="pagination" class="tabulator-footer"></div>
        </div>
        <div ref="table"></div>
    </div>
</template>

<script>
import Tabulator from 'tabulator-tables';

import '../styles/_tabulator.scss';
import { getDateFromW3c } from 'relapp-helpers';

export default {
    props: {
        tableData: {
            type: Array,
            required: true
        }
    },
    /**
     * Add all the properties found in the data object to Vue’s reactivity system.
     *
     * @return {object}
     */
    data() {
        return {
            tabulator: null,
            columns: [
                {
                    title: trans('subscriptions.customer'),
                    field: 'customer',
                    headerFilter: 'input',
                    headerFilterParams: { values: true },
                    headerFilterPlaceholder: 'filter'
                },
                {
                    title: trans('subscriptions.domain'),
                    field: 'domain',
                    headerFilter: 'input',
                    headerFilterParams: { values: true },
                    headerFilterPlaceholder: 'filter',
                    width: 350
                },
                {
                    title: trans('subscriptions.type'),
                    field: 'type',
                    formatter: cell => this.typeFormatter(cell.getValue()),
                    headerFilter: 'select',
                    headerFilterParams: this.typeFilter,
                    headerFilterPlaceholder: 'filter'
                },
                {
                    title: trans('subscriptions.product-name'),
                    field: 'product_name',
                    headerFilter: 'select',
                    headerFilterParams: { values: true },
                    headerFilterPlaceholder: 'filter'
                },
                {
                    title: trans('subscriptions.status'),
                    field: 'administrative_status',
                    formatter: cell => this.statusFormatter(cell.getValue()),
                    headerFilter: 'select',
                    headerFilterParams: this.statusFilter,
                    headerFilterPlaceholder: 'filter',
                    width: 100
                },
                {
                    title: trans('subscriptions.period'),
                    field: 'period',
                    headerFilter: 'select',
                    headerFilterParams: { values: true },
                    headerFilterPlaceholder: 'filter',
                    width: 110
                },
                {
                    title: trans('subscriptions.end_date'),
                    field: 'end_date',
                    formatter: cell => this.dateFormatter(cell.getValue()),
                    headerFilter: 'select',
                    headerFilterParams: { values: true },
                    headerFilterPlaceholder: 'filter',
                    width: 130
                }
            ]
        };
    },
    watch: {
        tableData: {
            /**
             * Watcher for the tableData
             *
             * @param {array} newData
             */
            handler(newData) {
                this.tabulator.replaceData(newData);
            },
            deep: true
        }
    },
    metaInfo: {
        title: trans('page.subscriptions.title')
    },
    /**
     * Called after the instance has been mounted.
     */
    mounted() {
        this.tabulator = new Tabulator(this.$refs.table, {
            ...tabulatorConfig.default,
            paginationElement: this.$refs.pagination,
            columns: this.columns,
            data: this.tableData,
            selectable: false,
            index: 'domain',
            langs: {
                nl: {
                    pagination: {
                        page_size: trans('pagination.results'),
                        first: trans('pagination.first'),
                        first_title: trans('pagination.first_title'),
                        last: trans('pagination.last'),
                        last_title: trans('pagination.last_title'),
                        prev: trans('pagination.previous'),
                        prev_title: trans('pagination.previous_title'),
                        next: trans('pagination.next'),
                        next_title: trans('pagination.next_title')
                    }
                }
            }
        });

        this.tabulator.setLocale('nl-nl');
    },
    methods: {
        /**
         * Download the contents of the table in CSV format.
         */
        downloadCsv() {
            this.tabulator.download('csv', 'data.csv');
        },
        /**
         * Tabulator formatter for types.
         *
         * @param  {string} value
         *
         * @return {any}
         */
        typeFormatter(value) {
            if (value === 'extension') {
                return trans('subscriptions.table.type-extension');
            }

            if (value === 'other') {
                return trans('subscriptions.table.type-other');
            }

            return value;
        },
        /**
         * Tabulator formatter for types.
         *
         * @param  {string} value
         *
         * @return {any}
         */
        statusFormatter(value) {
            if (value === 'active') {
                return trans('subscriptions.table.status-active');
            }

            if (value === 'canceled') {
                return trans('subscriptions.table.status-canceled');
            }

            if (value === 'deleted') {
                return trans('subscriptions.table.status-deleted');
            }

            return value;
        },
        /**
         * Tabulator formatter for dates.
         *
         * @param  {string} value
         *
         * @return {any}
         */
        dateFormatter(value) {
            return getDateFromW3c(value);
        },
        /**
         * Get the filter parameters for the type column.
         *
         * @return {object}
         */
        typeFilter() {
            let values = {};

            this.tableData.forEach(row => {
                values[row.type] = this.typeFormatter(row.type);
            });

            return { values };
        },
        /**
         * Get the filter parameters for the status column.
         *
         * @return {object}
         */
        statusFilter() {
            let values = {};

            this.tableData.forEach(row => {
                values[row.administrative_status] = this.statusFormatter(row.administrative_status);
            });

            return { values };
        }
    }
};
</script>
